import React, { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import IconMoreHorizontal from "../dynamicicons/icon-more-horizontal";
import VelavuIconButton from "./velavu-icon-button";
import * as styles from "./velavu-menu.module.scss";
import { classArr } from "../helper/style-helper";
import PopoverPanel from "./popover-panel";
import whitelabel from "../whitelabel";

interface VelavuMenuProps {
	style?: CSSProperties;
	className?: string;

	open: boolean;
	onClose: VoidFunction;

	refPosition: HTMLElement | null;

	children?: React.ReactNode;

	align?: "left" | "right";
}

export default function VelavuMenu(props: VelavuMenuProps) {
	if (!props.open) return null;

	return (
		<PopoverPanel
			refPosition={props.refPosition}
			onClose={props.onClose}
			horizontalAlign={props.align}
		>
			<div className={classArr(props.className, styles.container)}>
				{props.children}
			</div>
		</PopoverPanel>
	);
}

interface VelavuMenuItemProps {
	icon?: (size: number, color: string) => React.ReactNode;
	label: string;
	danger?: boolean;
	disabled?: boolean;
	onClick?: VoidFunction;
}

export function VelavuMenuItem(props: VelavuMenuItemProps) {
	return (
		<div
			className={classArr(styles.item, props.disabled && styles.disabled)}
			onClick={props.disabled ? undefined : props.onClick}
		>
			{props.icon && (
				<>
					{props.icon(
						16,
						props.danger
							? whitelabel.palette.error900
							: whitelabel.palette.secondary700,
					)}
					<div className={styles.itemIconSpacer} />
				</>
			)}
			<span
				className={
					props.danger
						? styles.itemLabelDanger
						: styles.itemLabelStandard
				}
			>
				{props.label}
			</span>
		</div>
	);
}

interface VelavuOverflowButtonProps {
	className?: string;
	style?: CSSProperties;

	disabled?: boolean;
	open?: boolean;
	tooltip?: string;
	onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export const VelavuOverflowButton = React.forwardRef<
	HTMLButtonElement,
	VelavuOverflowButtonProps
>((props, ref) => {
	const { t } = useTranslation();
	return (
		<VelavuIconButton
			ref={ref}
			style={props.style}
			className={props.className}
			tooltip={props.tooltip ?? t("common.more")}
			focus={props.open}
			onClick={props.onClick}
			disabled={props.disabled}
		>
			<IconMoreHorizontal color={whitelabel.palette.secondary700} />
		</VelavuIconButton>
	);
});
